import React, {Dispatch, SetStateAction} from 'react'
import {HStack, useMultiStyleConfig, StackProps} from '@chakra-ui/react'
import {FilterButton, GridLayoutControls, SortByWrapper} from '_uiComponents'
import {useViewport} from '_customHooks'

type LitingType = 'grid3' | 'grid2'
type SortingType = {id: string; label: string}

interface RefinementsGroupProps {
    sortingOptions: SortingType[] | undefined
    selectedSortingOptionLabel?: SortingType
    listingLayout: LitingType
    isFilterListOpened: boolean
    selectedFilters?: number
    filterTotals?: number
    isSearch?: boolean
    setListingLayout: Dispatch<SetStateAction<LitingType>>
    setIsFilterListOpened: Dispatch<SetStateAction<boolean>>
    limit?: number
}

/**
 * @name RefinementsGroup
 *
 * @component
 *
 * @description
 * This component is responsible for rendering a group of controls for refining
 * product listings, including filter controls, sorting options, and grid layout controls.
 * It also manages the visibility of these controls based on the viewport and scroll behavior.
 *
 * @param {RefinementsGroupProps} props - The props for the RefinementsGroup component.
 * @param {Array} props.sortingOptions - An array of sorting options to display.
 * @param {string} props.selectedSortingOptionLabel - The label of the currently selected sorting option.
 * @param {string} props.listingLayout - The current layout of the product listing (e.g., grid or list).
 * @param {boolean} props.isFilterListOpened - A flag indicating whether the filter list is opened.
 * @param {Array} props.selectedFilters - An array of selected filters.
 * @param {Object} props.filterTotals - An object containing the totals for each filter.
 * @param {boolean} [props.isSearch=false] - A flag indicating whether the component is being used in a search context.
 * @param {Function} props.setListingLayout - A function to update the listing layout.
 * @param {Function} props.setIsFilterListOpened - A function to toggle the filter list visibility.
 * @param {number} [props.limit] - Limit number value.
 *
 * @returns {JSX.Element} The rendered `RefinementsGroup` component.
 *
 * @example
 * ```tsx
 * <RefinementsGroup
 *   sortingOptions={sortingOptions}
 *   selectedSortingOptionLabel="Price: Low to High"
 *   listingLayout="grid"
 *   isFilterListOpened={false}
 *   selectedFilters={selectedFilters}
 *   filterTotals={filterTotals}
 *   isSearch={false}
 *   setListingLayout={setListingLayout}
 *   setIsFilterListOpened={setIsFilterListOpened}
 * />
 * ```
 */
const RefinementsGroup = React.memo(
    ({
        sortingOptions,
        selectedSortingOptionLabel,
        listingLayout,
        isFilterListOpened,
        selectedFilters,
        filterTotals,
        isSearch = false,
        setListingLayout,
        setIsFilterListOpened,
        limit,
    }: RefinementsGroupProps) => {
        const styles = useMultiStyleConfig('RefinementsGroup', {isSearch}) as Record<string, StackProps>
        const {isMobile} = useViewport()

        return (
            <HStack {...styles.wrapper} data-testid="refinements-group">
                <HStack {...styles.filterControls} data-testid="filter-controls">
                    <FilterButton
                        isFilterListOpened={isFilterListOpened}
                        setIsFilterListOpened={setIsFilterListOpened}
                        selectedFilters={selectedFilters}
                        filterTotals={filterTotals}
                    />
                    {sortingOptions && <SortByWrapper sortingOptions={sortingOptions} selectedSortingOptionLabel={selectedSortingOptionLabel} limit={limit} />}
                </HStack>
                {(!isSearch || (isSearch && !isMobile)) && (
                    <HStack {...styles.gridControls}>
                        <GridLayoutControls listingLayout={listingLayout} setListingLayout={setListingLayout} />
                    </HStack>
                )}
            </HStack>
        )
    },
)

RefinementsGroup.displayName = 'RefinementsGroup'

export {RefinementsGroup}
